import { HEALTH_CARD_PHOTO_MEDIA_TYPE } from "@emr/contracts";
import { useQuery } from "@tanstack/react-query";
import { type ChangeEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  type HealthCardPhotoCaptureClient,
  type HealthCardPhotosClient,
  healthCardPhotoSettingsKey,
} from "../api/healthCardPhotos";
import {
  healthCardPhotoCaptureSupported,
  healthCardPhotoProblemText,
  type PreparedHealthCardPhoto,
  prepareHealthCardPhoto,
} from "../lib/healthCardPhoto";
import "./health-card-photo.css";

/**
 * Whether the desk can take a health card photo now (PA-13): this browser can prepare one, and the
 * clinic's settings say a photo checker is running (`capture: "available"`). A failed or refused
 * settings read (an older agent-api, a role without access) means no.
 */
export function useHealthCardPhotoCapture(
  client: HealthCardPhotoCaptureClient,
  siteId: string,
): boolean {
  const [supported] = useState(healthCardPhotoCaptureSupported);
  const settings = useQuery({
    queryKey: healthCardPhotoSettingsKey(siteId),
    queryFn: ({ signal }) => client.getHealthCardPhotoSettings(signal),
    enabled: supported,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return supported && settings.data?.capture === "available";
}

/** A blob: URL for the bytes while they are on screen, revoked when they change or leave. */
function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (blob === null) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

/**
 * **Take photo** (the camera, where the device has one) and **Choose file**, each a file input
 * covering its button. The picture is prepared in the browser (downscaled, re-encoded, metadata
 * dropped, checked) before `onPrepared`; "Preparing the photo…" and any problem show on the row.
 */
export function HealthCardPhotoPicker({
  takeLabel,
  chooseLabel,
  disabled = false,
  onPrepared,
}: {
  readonly takeLabel: string;
  readonly chooseLabel: string;
  readonly disabled?: boolean;
  readonly onPrepared: (photo: PreparedHealthCardPhoto) => void;
}) {
  const [preparing, setPreparing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const attempt = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const pick = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // The same picture can be chosen again after a problem.
    event.target.value = "";
    if (file === undefined) return;
    attempt.current += 1;
    const current = attempt.current;
    const latest = () => mounted.current && current === attempt.current;
    setPreparing(true);
    setProblem(null);
    try {
      const photo = await prepareHealthCardPhoto(file);
      if (latest()) onPrepared(photo);
    } catch (caught) {
      if (latest()) setProblem(healthCardPhotoProblemText(caught));
    } finally {
      if (latest()) setPreparing(false);
    }
  };

  const off = disabled || preparing;
  return (
    <>
      <label className="hcp-btn hcp-pick">
        <input
          type="file"
          accept="image/*"
          capture="environment"
          disabled={off}
          onChange={(event) => void pick(event)}
        />
        {takeLabel}
      </label>
      <label className="hcp-btn hcp-pick">
        <input type="file" accept="image/*" disabled={off} onChange={(event) => void pick(event)} />
        {chooseLabel}
      </label>
      {preparing ? (
        <span className="hcp-state" role="status">
          Preparing the photo…
        </span>
      ) : null}
      {problem !== null ? (
        <span className="hcp-problem" role="alert">
          {problem}
        </span>
      ) : null}
    </>
  );
}

/**
 * The registration forms' optional photo of the front of the health card (Patients page and
 * Add walk-in, PA-13). It renders only while capture is available; the form keeps the photo and
 * sends it once the chart exists, so a photo never holds up a registration. The typed number
 * stays required.
 */
export function HealthCardPhotoField({
  client,
  siteId,
  photo,
  onPhotoChange,
  disabled = false,
}: {
  readonly client: HealthCardPhotoCaptureClient;
  readonly siteId: string;
  readonly photo: PreparedHealthCardPhoto | null;
  readonly onPhotoChange: (photo: PreparedHealthCardPhoto | null) => void;
  readonly disabled?: boolean;
}) {
  const available = useHealthCardPhotoCapture(client, siteId);
  const hintId = useId();
  const blob = useMemo(
    () => (photo === null ? null : new Blob([photo.bytes], { type: HEALTH_CARD_PHOTO_MEDIA_TYPE })),
    [photo],
  );
  const preview = useObjectUrl(blob);
  if (!available) return null;
  return (
    <fieldset className="hcp hcp-field" aria-describedby={hintId}>
      <legend className="hcp-title">Health card photo (optional)</legend>
      <p id={hintId} className="hcp-sub">
        The front of the card, saved to the chart after registering. Type the number too.
      </p>
      <div className="hcp-row">
        {photo !== null && preview !== null ? (
          <img
            className="hcp-preview"
            src={preview}
            alt="Front of the health card, ready to save"
          />
        ) : null}
        {photo !== null ? <span className="hcp-state">Photo ready</span> : null}
        <HealthCardPhotoPicker
          takeLabel={photo === null ? "Take photo" : "Retake photo"}
          chooseLabel={photo === null ? "Choose file" : "Choose another file"}
          disabled={disabled}
          onPrepared={onPhotoChange}
        />
        {photo !== null ? (
          <button
            type="button"
            className="hcp-btn"
            disabled={disabled}
            onClick={() => onPhotoChange(null)}
          >
            Remove photo
          </button>
        ) : null}
      </div>
    </fieldset>
  );
}

type PhotoView =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly url: string }
  | { readonly status: "failed" };

/**
 * A stored photo on screen (Review intake, the chart's Details). Each mount is one audited read of
 * the bytes; the blob: URL is revoked when it leaves the screen, so closing and reopening reads it
 * again and nothing stays in memory.
 */
export function HealthCardPhotoImage({
  client,
  photoId,
  alt,
}: {
  readonly client: Pick<HealthCardPhotosClient, "openHealthCardPhoto">;
  readonly photoId: string;
  readonly alt: string;
}) {
  const [view, setView] = useState<PhotoView>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  // The latest route, without re-reading (another audited read) when the client object changes.
  const open = useRef(client.openHealthCardPhoto);
  open.current = client.openHealthCardPhoto;
  useEffect(() => {
    void attempt;
    const controller = new AbortController();
    let url: string | null = null;
    setView({ status: "loading" });
    open
      .current(photoId, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        url = URL.createObjectURL(blob);
        setView({ status: "ready", url });
      })
      .catch(() => {
        if (!controller.signal.aborted) setView({ status: "failed" });
      });
    return () => {
      controller.abort();
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [photoId, attempt]);

  if (view.status === "loading") {
    return (
      <p className="hcp-meta" role="status">
        Loading the photo…
      </p>
    );
  }
  if (view.status === "failed") {
    return (
      <p className="hcp-problem" role="alert">
        The photo could not be shown.{" "}
        <button type="button" className="hcp-btn" onClick={() => setAttempt((value) => value + 1)}>
          Try again
        </button>
      </p>
    );
  }
  return <img className="hcp-photo" src={view.url} alt={alt} />;
}
